import {
  BarChart3,
  ListTree,
  ListFilter,
  Bell,
  BookOpen,
  BookText,
  ListChecks,
  Scale,
  TrendingUp,
  TriangleAlert,
  Globe,
  Building2,
  ClipboardList,
  CreditCard,
  FileMinus,
  FileText,
  Home,
  Inbox,
  Landmark,
  Mail,
  Package,
  Percent,
  Settings2,
  ReceiptText,
  Repeat2,
  Send,
  ShieldCheck,
  Truck,
  Store,
  Users,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import { ACCOUNTING_CONNECTION_MODULES, E_INVOICING_REGISTRATION_MODULES, type ModuleKey } from "@/lib/modules";

export type AreaId = "receivables" | "payables" | "expenses" | "books";

export interface NavPage {
  label: string;
  icon: LucideIcon;
  to: string;
  /** Override of `label` when rendered in the global command menu. */
  commandLabel?: string;
  /** Override of `to` for the command menu, e.g. a filtered view the label promises. */
  commandTo?: string;
  commandKeywords?: string[];
  /** Module needed beyond the area's own, e.g. payment pages. Given several, any one will do. */
  module?: ModuleKey | readonly ModuleKey[];
  /** `to` of the page this one nests under in the sidebar, shown while that group is open. */
  parent?: string;
}

export interface LiveAreaConfig {
  kind: "live";
  id: AreaId;
  label: string;
  icon: LucideIcon;
  to: string;
  /** Module the client must hold for this area to be usable at all. */
  module: ModuleKey;
  /** Hidden everywhere, not grayed, unless the client is known to hold the module. */
  hiddenWithoutModule?: boolean;
  pages: NavPage[];
}

export interface SoonAreaConfig {
  kind: "soon";
  label: string;
  icon: LucideIcon;
}

export type AreaConfig = LiveAreaConfig | SoonAreaConfig;

export const AGENT_AREAS: AreaConfig[] = [
  {
    kind: "live",
    id: "receivables",
    label: "Receivables",
    icon: FileText,
    to: "/ar",
    module: "ar",
    pages: [
      {
        label: "Home",
        commandLabel: "Receivables home",
        commandKeywords: ["ar", "overview"],
        icon: Home,
        to: "/ar",
      },
      { label: "Invoices", icon: FileText, to: "/invoices" },
      {
        label: "Credit notes",
        commandLabel: "Credit notes",
        commandKeywords: ["credit", "refund", "adjustment", "cn"],
        icon: FileMinus,
        to: "/credit-notes",
      },
      {
        label: "Recurring invoices",
        commandLabel: "Recurring invoices",
        commandKeywords: ["recurring", "subscription", "contract", "retainer", "schedule"],
        icon: Repeat2,
        to: "/schedules",
      },
      {
        label: "E-invoices",
        commandLabel: "Sent e-invoices",
        commandKeywords: ["peppol", "sdi", "e-invoicing", "einvoicing", "network", "sent", "delivery"],
        icon: Globe,
        to: "/sent-e-invoices",
        // Its own module, not Receivables': sending over a network is priced separately from invoicing
        // and arrives with a country's mandate rather than with the rest of AR.
        module: "ar.e_invoicing",
      },
      { label: "Customers", icon: Users, to: "/customers" },
      { label: "Products", icon: Package, to: "/products" },
      { label: "Tax rates", icon: Percent, to: "/tax-rates" },
      {
        label: "Payment settings",
        icon: CreditCard,
        to: "/settings/payments",
        module: "ar.payments",
      },
      {
        label: "Accounting",
        commandLabel: "Invoice accounting",
        commandKeywords: ["xero", "quickbooks", "books", "income account", "deposit account", "tax mapping", "sync"],
        icon: Landmark,
        to: "/ar/accounting",
        module: "ar.accounting",
      },
    ],
  },
  {
    kind: "live",
    id: "payables",
    label: "Payables",
    icon: Send,
    to: "/ap",
    module: "ap",
    pages: [
      {
        label: "Home",
        commandLabel: "Payables home",
        commandKeywords: ["ap", "overview"],
        icon: Home,
        to: "/ap",
      },
      { label: "Bills", icon: FileText, to: "/bills" },
      {
        label: "Purchase orders",
        commandLabel: "Purchase orders",
        commandKeywords: ["po", "purchase", "order", "matching", "three-way"],
        icon: ClipboardList,
        to: "/purchase-orders",
      },
      {
        label: "Delivery notes",
        commandLabel: "Delivery notes",
        commandKeywords: ["dn", "delivery", "goods received", "grn", "receipt", "matching", "three-way"],
        icon: Truck,
        to: "/delivery-notes",
      },
      { label: "Suppliers", icon: Users, to: "/suppliers" },
      { label: "Supplier credit notes", icon: FileMinus, to: "/supplier-credit-notes" },
      {
        label: "Approval policies",
        commandLabel: "Bill approval policies",
        commandKeywords: ["reviewers", "approval", "rules", "auto-approve", "void"],
        icon: ShieldCheck,
        to: "/bills/approval-policies",
      },
      {
        label: "E-invoicing",
        commandLabel: "Received e-invoices",
        commandKeywords: ["peppol", "e-invoicing", "einvoicing", "network", "received", "documents"],
        icon: Globe,
        to: "/received-e-invoices",
        // Its own module, not Payables'. Receiving over a network is priced separately and is the half
        // of AP a client can adopt long after the rest - mandates arrive per country, not per customer.
        module: "ap.e_invoicing",
      },
      { label: "Email forwarding", icon: Mail, to: "/bills/email-forwarding" },
      { label: "Gmail integration", icon: Inbox, to: "/settings/integrations/gmail" },
      {
        label: "Accounting",
        commandLabel: "Bill accounting",
        commandKeywords: ["xero", "quickbooks", "books", "expense account", "bill payment account", "sync"],
        icon: Landmark,
        to: "/ap/accounting",
        module: "ap.accounting",
      },
    ],
  },
  {
    kind: "live",
    id: "expenses",
    label: "Expenses",
    icon: Wallet,
    to: "/exp",
    module: "expenses",
    pages: [
      {
        label: "Home",
        commandLabel: "Expenses home",
        commandKeywords: ["expenses", "overview", "spend", "report", "vat", "receipts"],
        icon: Home,
        to: "/exp",
      },
      {
        label: "Expenses",
        commandLabel: "Expenses",
        commandKeywords: ["card", "spend", "expense", "reconcile"],
        icon: Wallet,
        to: "/expenses",
      },
      {
        label: "Card transactions",
        commandLabel: "Card transactions",
        commandKeywords: ["card", "transactions", "feed", "statement"],
        icon: CreditCard,
        to: "/card-transactions",
      },
      {
        label: "Receipts",
        commandLabel: "Receipts",
        commandKeywords: ["receipt", "upload", "inbox", "vat"],
        icon: ReceiptText,
        to: "/receipts",
      },
      {
        label: "Cards",
        commandLabel: "Linked cards",
        commandKeywords: ["card", "connection", "link", "enrol"],
        icon: CreditCard,
        to: "/cards",
      },
      {
        label: "Merchants",
        commandLabel: "Merchants",
        commandKeywords: ["merchant", "vendor", "shop", "spend", "where"],
        icon: Store,
        to: "/expenses/merchants",
      },
      {
        label: "Approval policies",
        commandLabel: "Expense approval policies",
        commandKeywords: ["approval", "policy", "rules", "auto-approve", "flag", "expense policy", "document"],
        icon: ShieldCheck,
        to: "/expenses/approval-policies",
      },
      {
        label: "Category mapping",
        commandLabel: "Merchant category mapping",
        commandKeywords: ["mcc", "mapping", "category", "account", "coding"],
        icon: ClipboardList,
        to: "/expenses/mcc-mappings",
      },
    ],
  },
  {
    // D35: the internal general ledger. Setup is reached from Home's checklist, not the nav: it is Admin-only.
    kind: "live",
    id: "books",
    label: "Books",
    icon: BookOpen,
    to: "/books",
    module: "books",
    // Books is offered to chosen clients only, so the others never see it.
    hiddenWithoutModule: true,
    pages: [
      {
        label: "Home",
        commandLabel: "Books home",
        commandKeywords: ["books", "general ledger", "gl", "overview", "setup"],
        icon: Home,
        to: "/books",
      },
      {
        label: "Bank transactions",
        commandLabel: "Bank transactions to review",
        commandTo: "/books/bank-lines?status=Uncategorised",
        commandKeywords: ["bank", "uncategorised", "categorise", "match", "reconcile", "transactions"],
        icon: Landmark,
        to: "/books/bank-lines",
      },
      {
        label: "Reports",
        commandLabel: "Reports overview",
        commandKeywords: ["reports", "reporting", "insights", "overview", "cash", "revenue", "expenses"],
        icon: BarChart3,
        to: "/books/reports",
      },
      {
        label: "Profit and loss",
        commandKeywords: ["p&l", "pnl", "profit", "loss", "income statement"],
        icon: TrendingUp,
        to: "/books/profit-and-loss",
        parent: "/books/reports",
      },
      {
        label: "Balance sheet",
        commandKeywords: ["balance sheet", "assets", "liabilities", "equity"],
        icon: ListChecks,
        to: "/books/balance-sheet",
        parent: "/books/reports",
      },
      {
        label: "Cash flow",
        commandLabel: "Cash flow analysis",
        commandKeywords: ["cash flow", "cash", "bank balance", "movement", "runway"],
        icon: Wallet,
        to: "/books/cash-flow",
        parent: "/books/reports",
      },
      {
        label: "Trial balance",
        commandKeywords: ["trial balance", "tb", "drift", "platform", "xero", "quickbooks"],
        icon: Scale,
        to: "/books/trial-balance",
        parent: "/books/reports",
      },
      {
        label: "General ledger",
        commandKeywords: ["general ledger", "gl", "journal", "entries"],
        icon: BookText,
        to: "/books/ledger",
        parent: "/books/reports",
      },
      {
        label: "Chart of accounts",
        commandKeywords: ["chart of accounts", "accounts", "ledger accounts", "coa", "balances"],
        icon: ListTree,
        to: "/books/accounts",
        parent: "/books/reports",
      },
      {
        label: "Posting issues",
        commandKeywords: ["posting", "failed", "skipped", "waiting", "retry"],
        icon: TriangleAlert,
        to: "/books/postings",
      },
      {
        label: "Rules",
        commandLabel: "Categorisation rules",
        commandKeywords: ["rules", "auto-categorise", "categorisation", "always file", "bank rules"],
        icon: ListFilter,
        to: "/books/rules",
      },
      {
        label: "Settings",
        commandLabel: "Books settings",
        commandKeywords: ["close date", "lock", "close books", "control accounts"],
        icon: Settings2,
        to: "/books/settings",
      },
    ],
  },
  { kind: "soon", label: "Payroll", icon: CreditCard },
];

/** Serves every area; sits apart at the foot of the nav and is never gated as a whole. */
export interface OrgAreaConfig {
  id: "organisation";
  label: string;
  icon: LucideIcon;
  to: string;
  pages: NavPage[];
}

export const ORG_AREA: OrgAreaConfig = {
  id: "organisation",
  label: "Organisation",
  icon: Building2,
  to: "/organisation/settings",
  pages: [
    {
      label: "Details",
      commandLabel: "Organisation details",
      commandKeywords: ["organisation", "business", "address", "branding", "logo", "currency", "settings"],
      icon: Building2,
      to: "/organisation/settings",
    },
    {
      label: "AR settings",
      commandLabel: "AR settings",
      commandKeywords: ["payment terms", "late fee", "reminders", "invoice numbering", "bank details", "tone"],
      icon: Settings2,
      to: "/organisation/settings/ar",
    },
    {
      label: "E-invoicing",
      commandLabel: "E-invoicing registration",
      commandKeywords: ["peppol", "e-invoicing", "einvoicing", "network", "register", "participant"],
      icon: Globe,
      to: "/organisation/settings/e-invoicing",
      module: E_INVOICING_REGISTRATION_MODULES,
    },
    {
      // The connection itself and what it synced. How each area posts to it lives in that area's own
      // Accounting page.
      label: "Accounting",
      commandLabel: "Accounting connection",
      commandKeywords: [
        "xero",
        "quickbooks",
        "accounting",
        "books",
        "chart of accounts",
        "sync",
        "tax rates",
        "tracking categories",
      ],
      icon: Landmark,
      to: "/organisation/settings/accounting",
      module: ACCOUNTING_CONNECTION_MODULES,
    },
    {
      // The organisation's own bank accounts, read-only over open banking. Serves every area, so it sits here.
      label: "Bank connections",
      commandLabel: "Bank connections",
      commandKeywords: ["bank", "open banking", "balance", "account", "connect", "yapily", "feed"],
      icon: Wallet,
      to: "/organisation/bank-connections",
      module: "banking",
    },
    {
      // Per user, not per organisation: where an approver links their own phone for bill approvals on WhatsApp.
      label: "My notifications",
      commandLabel: "My WhatsApp notifications",
      commandKeywords: ["whatsapp", "notifications", "my phone", "bill approvals", "enrol"],
      icon: Bell,
      to: "/account/notifications",
      module: "whatsapp",
    },
    { label: "Users", icon: Users, to: "/organisation/users" },
  ],
};
